export const TIME_ENTRY_DESCRIPTION_MAX_LENGTH = 255;

export const TIME_ENTRY_HOURLY_RATE_MAX_DIGITS = 15;

export const TIME_ENTRY_HOURLY_RATE_MAX_VALUE = 999_999_999_999_999;

/** Max duration (hours) allowed in manual time entry UI (matches backend-friendly scale). */
export const TIME_ENTRY_DURATION_MAX_VALUE = 99_999_999.99;

/**
 * Max total billable amount (duration × hourly rate) so stored amount stays within the same numeric scale
 * as duration and avoids silent save/list issues when the product exceeds storage limits.
 */
export const TIME_ENTRY_TOTAL_AMOUNT_MAX_VALUE = 9_999_999_999.99;

/**
 * Max value for duration (hours) or hourly rate in manual entry UI: 10 integer digits and 2 decimal places.
 */
export const TIME_ENTRY_DURATION_OR_RATE_INPUT_MAX_VALUE = 9_999_999_999.99;

/** Max characters for duration text at that scale (10 + '.' + 2). */
export const TIME_ENTRY_DURATION_INPUT_MAX_LENGTH = 13;

/**
 * Parses hourly rate from API or UI (number or locale/currency string) for cap math.
 *
 * @param {unknown} hourlyRate
 * @returns {number|null} finite positive rate, or null if missing/invalid
 */
export function parseTimeEntryHourlyRate(hourlyRate) {
  if (hourlyRate === null || hourlyRate === undefined || hourlyRate === '') {
    return null;
  }
  if (typeof hourlyRate === 'number') {
    return Number.isFinite(hourlyRate) && hourlyRate > 0 ? hourlyRate : null;
  }
  const cleaned = String(hourlyRate).trim().replace(/\$/g, '').replace(/,/g, '');
  const n = Number(cleaned);
  return Number.isFinite(n) && n > 0 ? n : null;
}

/**
 * Returns the largest duration (hours, one decimal place) allowed for a given hourly rate
 * so that duration × rate does not exceed {@link TIME_ENTRY_TOTAL_AMOUNT_MAX_VALUE}.
 *
 * @param {number|string} hourlyRate
 * @returns {number}
 */
export function getMaxDurationHoursForRate(hourlyRate) {
  const r = parseTimeEntryHourlyRate(hourlyRate);
  if (r == null) {
    return TIME_ENTRY_DURATION_MAX_VALUE;
  }
  const maxByTotal = TIME_ENTRY_TOTAL_AMOUNT_MAX_VALUE / r;
  const capped = Math.min(TIME_ENTRY_DURATION_MAX_VALUE, maxByTotal);
  return Math.floor(capped * 10 + 1e-9) / 10;
}

/**
 * Returns the largest hourly rate allowed for a given duration so total amount does not exceed
 * {@link TIME_ENTRY_TOTAL_AMOUNT_MAX_VALUE}, capped by {@link TIME_ENTRY_HOURLY_RATE_MAX_VALUE}.
 *
 * @param {number|string} durationHours
 * @returns {number}
 */
export function getMaxHourlyRateForDuration(durationHours) {
  const d = Number(durationHours);
  if (!Number.isFinite(d) || d <= 0) {
    return TIME_ENTRY_HOURLY_RATE_MAX_VALUE;
  }
  const maxByTotal = TIME_ENTRY_TOTAL_AMOUNT_MAX_VALUE / d;
  return Math.min(TIME_ENTRY_HOURLY_RATE_MAX_VALUE, maxByTotal);
}

/**
 * Formats a capped duration value for the duration text input (up to one decimal place).
 *
 * @param {number} hours
 * @returns {string}
 */
export function formatDurationInputFromHours(hours) {
  if (!Number.isFinite(hours)) {
    return '';
  }
  const roundedTenth = Math.floor(hours * 10 + 1e-9) / 10;
  return Number.isInteger(roundedTenth) ? String(roundedTenth) : roundedTenth.toFixed(1);
}

/**
 * Whether duration × hourly rate would exceed {@link TIME_ENTRY_TOTAL_AMOUNT_MAX_VALUE}.
 * Uses division to reduce floating-point overflow on very large products.
 *
 * @param {number|string} durationHours
 * @param {number|string} hourlyRate
 * @returns {boolean}
 */
export function exceedsTimeEntryTotalAmountMax(durationHours, hourlyRate) {
  const d = Number(durationHours);
  const r = parseTimeEntryHourlyRate(hourlyRate);
  if (!Number.isFinite(d) || r == null || d <= 0) {
    return false;
  }
  return r > TIME_ENTRY_TOTAL_AMOUNT_MAX_VALUE / d;
}

/**
 * If current duration text would exceed the total-amount cap at the given hourly rate,
 * returns the capped duration string for the input; otherwise returns the previous value.
 *
 * @param {string|undefined} prevDuration
 * @param {unknown} hourlyRate
 * @returns {string|undefined}
 */
export function clampDurationInputToTotalCap(prevDuration, hourlyRate) {
  if (prevDuration === '' || prevDuration === undefined) return prevDuration;
  const d = parseFloat(prevDuration);
  if (Number.isNaN(d)) return prevDuration;
  if (!exceedsTimeEntryTotalAmountMax(d, hourlyRate)) return prevDuration;
  return formatDurationInputFromHours(getMaxDurationHoursForRate(hourlyRate));
}
